<!--
  Copyright (c) 2025 NoorWays. All rights reserved.
  Licensed under the terms specified in the LICENSE.txt file.
-->

<template>
  <div id="app" class="app" data-noorwriter-web-app="true">
    <AppNavigation />
    <main id="main-content">
      <router-view v-slot="{ Component }">
        <transition name="page" mode="out-in">
          <component :is="Component" />
        </transition>
      </router-view>
    </main>
    <!-- Hide footer on editor page for maximized workspace -->
    <AppFooter v-if="!isEditorPage" />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { useHead } from '@vueuse/head'
import { useI18n } from 'vue-i18n'
import AppNavigation from '@/components/AppNavigation.vue'
import AppFooter from '@/components/AppFooter.vue'

const route = useRoute()
const { t, locale } = useI18n()

// Hide footer on editor page for maximized workspace experience
const isEditorPage = computed(() => route.path === '/editor')

// Set up comprehensive SEO head meta information
useHead({
  title: () => t('seo.homepage.title'),
  meta: [
    // Basic SEO
    {
      name: 'description',
      content: () => t('seo.homepage.description'),
    },
    {
      name: 'keywords',
      content: () => t('seo.homepage.keywords'),
    },
    {
      name: 'viewport',
      content: 'width=device-width, initial-scale=1.0',
    },
    {
      name: 'language',
      content: () => locale.value,
    },

    // Open Graph / Facebook
    {
      property: 'og:type',
      content: 'website',
    },
    {
      property: 'og:title',
      content: () => t('seo.homepage.title'),
    },
    {
      property: 'og:description',
      content: () => t('seo.homepage.description'),
    },
    {
      property: 'og:url',
      content: 'https://n-writer.app',
    },
    {
      property: 'og:site_name',
      content: 'n.writer',
    },
    {
      property: 'og:image',
      content: 'https://n-writer.app/og-image.png',
    },
    {
      property: 'og:image:width',
      content: '1260',
    },
    {
      property: 'og:image:height',
      content: '630',
    },
    {
      property: 'og:locale',
      content: () => (locale.value === 'en' ? 'en_US' : locale.value === 'fr' ? 'fr_FR' : 'es_ES'),
    },

    // Twitter Card
    {
      name: 'twitter:card',
      content: 'summary_large_image',
    },
    {
      name: 'twitter:title',
      content: () => t('seo.homepage.title'),
    },
    {
      name: 'twitter:description',
      content: () => t('seo.homepage.description'),
    },
    {
      name: 'twitter:image',
      content: 'https://n-writer.app/og-image.png',
    },
    {
      name: 'twitter:site',
      content: '@noorwriter',
    },

    // Additional SEO
    {
      name: 'theme-color',
      content: '#1E40AF',
    },
    {
      name: 'msapplication-TileColor',
      content: '#1E40AF',
    },
    {
      name: 'application-name',
      content: 'n.writer',
    },
  ],
  link: [
    // Canonical URL
    {
      rel: 'canonical',
      href: 'https://n-writer.app',
    },
    // Alternate language versions
    {
      rel: 'alternate',
      hreflang: 'en',
      href: 'https://n-writer.app/en',
    },
    {
      rel: 'alternate',
      hreflang: 'fr',
      href: 'https://n-writer.app/fr',
    },
    {
      rel: 'alternate',
      hreflang: 'es',
      href: 'https://n-writer.app/es',
    },
    {
      rel: 'alternate',
      hreflang: 'x-default',
      href: 'https://n-writer.app',
    },
  ],
  script: [
    // JSON-LD Schema Markup
    {
      type: 'application/ld+json',
      children: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: 'n.writer',
        description: () => t('seo.homepage.description'),
        url: 'https://n-writer.app',
        applicationCategory: 'WritingApplication',
        operatingSystem: 'Web Browser',
        offers: {
          '@type': 'Offer',
          price: '0',
          priceCurrency: 'USD',
          description: 'Free AI writing assistant with premium options',
        },
        featureList: [
          'AI Grammar Checking',
          'Spell Checking',
          'Text Improvement',
          'Translation',
          'Proofreading',
          'Text Rewriting',
          'Content Summarization',
        ],
        creator: {
          '@type': 'Organization',
          name: 'NoorWays',
          url: 'https://noorways.fr',
        },
        inLanguage: ['en', 'fr', 'es'],
        isAccessibleForFree: true,
      }),
    },
  ],
})
</script>

<style lang="scss">
.app {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  font-family:
    'Manrope',
    -apple-system,
    BlinkMacSystemFont,
    'Segoe UI',
    Roboto,
    Oxygen,
    Ubuntu,
    Cantarell,
    sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

#main-content {
  flex: 1;
}

/* Page Transitions - Matching NoorWays site */
.page-enter-active,
.page-leave-active {
  transition: all 0.3s ease-in-out;
}

.page-enter-from,
.page-leave-to {
  opacity: 0;
  transform: translateY(10px);
}

/* Smooth scroll behavior */
html {
  scroll-behavior: smooth;
}
</style>
